// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { useCallback, useEffect, useState, useRef } from 'react';
import DatasetService from '../../../services/dataset/DatasetService';
import WorkspaceService from '../../../services/workspace/WorkspaceService';
import { useDatasets } from '../../../state/hooks/DatasetHooks';
import { useOrganizationId } from '../../../state/hooks/OrganizationHooks';
import { useWorkspaceId } from '../../../state/hooks/WorkspaceHooks';
import { DatasetsUtils } from '../../../utils';

export const useDatasetMapInitialization = (parameterValue, setParameterValue, scenario) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [datasetMapContent, setDatasetMapContent] = useState(null);
  const [noMapAvailable, setNoMapAvailable] = useState(false);
  const organizationId = useOrganizationId();
  const workspaceId = useWorkspaceId();
  const datasets = useDatasets();
  const hasUserInteracted = useRef(false);

  const initializeMapFromDataset = useCallback(async () => {
    setNoMapAvailable(false);

    if (parameterValue && parameterValue !== '') {
      return;
    }

    if (!scenario) {
      setNoMapAvailable(true);
      return;
    }

    if (!scenario?.datasetList || scenario.datasetList.length === 0) {
      setNoMapAvailable(true);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const datasetId = scenario.datasetList[0];
      let datasetObj = datasets?.find((d) => d.id === datasetId);

      if (!datasetObj) {
        try {
          const response = await DatasetService.findDatasetById(organizationId, datasetId);
          datasetObj = response.data;
        } catch (apiError) {
          setError(apiError);
          setNoMapAvailable(true);
          setIsLoading(false);
          return;
        }
      }

      if (!datasetObj) {
        setError(new Error('Dataset not found'));
        setNoMapAvailable(true);
        setIsLoading(false);
        return;
      }

      if (!datasetObj.connector || !datasetObj.connector.parametersValues) {
        setNoMapAvailable(true);
        setIsLoading(false);
        return;
      }

      // Get the storage file path from the dataset
      const storageFilePath = DatasetsUtils.getStorageFilePathFromDataset(datasetObj);

      if (!storageFilePath) {
        setNoMapAvailable(true);
        setIsLoading(false);
        return;
      }

      try {
        const fileContent = await WorkspaceService.downloadWorkspaceFileData(
          organizationId,
          workspaceId,
          storageFilePath
        );

        if (fileContent) {
          try {
            const parsedContent = JSON.parse(fileContent);
            if (
              !parsedContent ||
              ((!parsedContent.graph || Object.keys(parsedContent.graph).length === 0) &&
                (!parsedContent.tables || Object.keys(parsedContent.tables).length === 0))
            ) {
              setDatasetMapContent(fileContent);
            } else {
              setDatasetMapContent(fileContent);
            }
          } catch (parseError) {
            setError(new Error('Invalid map format in dataset'));
            setNoMapAvailable(true);
          }
        } else {
          setNoMapAvailable(true);
        }
      } catch (downloadError) {
        setError(downloadError);
        setNoMapAvailable(true);
      }
    } catch (error) {
      setError(error);
      setNoMapAvailable(true);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, workspaceId, datasets, scenario, parameterValue]);

  useEffect(() => {
    initializeMapFromDataset();

    return () => {
      setDatasetMapContent(null);
    };
  }, [initializeMapFromDataset]);

  const handleUserInteraction = useCallback(() => {
    if (!hasUserInteracted.current && datasetMapContent) {
      hasUserInteracted.current = true;
      setParameterValue(datasetMapContent);
    }
  }, [datasetMapContent, setParameterValue]);

  return {
    isLoading,
    error,
    datasetMapContent,
    noMapAvailable,
    handleUserInteraction,
  };
};
